import React from "react";
import Helmet from "react-helmet";
import styled from "styled-components";
import rehypeReact from "rehype-react";
import formatDistanceToNow from "date-fns/formatDistanceToNow";
import itLocale from "date-fns/locale/it";
// import Img from "gatsby-image";
import Figure from "../Figure/Figure";
import Product, { ProductSmall } from "../Product/Product";
import Slider from "../Slider/Slider";
// import Top from "../Top/Top";
import {
  H1,
  H2,
  H3,
  H4,
  Accordion as AccordionStyle,
  P,
  A,
  Ol,
  Ul,
  Blockquote,
  Toc
} from "../Typo/Typo";
import { logEvent, getTheme } from "../../utilities";

const Wrapper = styled.main`
  font-size: 18px;
  letter-spacing: 0;
  line-height: 1.75;
  margin: auto; // IE
  margin: calc(var(--gutter) * 2) auto calc(var(--gutter) * 4);
  max-width: 1000px; // IE
  max-width: var(--max-width);
  word-spacing: 0;
  display: flex;
  flex-direction: column;
  padding: 10px; // IE

  @supports (--var: 0) {
    padding: 0;
  }
`;

const Meta = styled.div`
  text-align: center;
  font-size: 13px;
  padding: 0 var(--gutter);
  margin-bottom: var(--gutter);
`;

const SliderEl = styled(Slider)`
  --product-width: 225px;
  order: 3;
  width: 100vw;
  transform: translateX(-50%);
  left: calc(50% - var(--gutter) / 2);
  max-width: calc(var(--max-width) + var(--arrow-size) * 2);
`;

const ProductWrapper = styled.div`
  margin: 0 auto;
  max-width: var(--content-width);
  width: calc(100vw - var(--gutter) * 2);
`;

const GatsbyIframe = styled.div``;

const CCProduct = props => (
  <ProductWrapper>
    <Product
      scope="post"
      product={{
        id: props["data-id"],
        brand: props["data-brand"],
        ctaLabel: props["data-cta-label"],
        cta: props["data-cta"],
        description: props["data-description"],
        imageId: props["data-image-id"],
        label: props["data-label"],
        price: props["data-price"],
        title: props["data-title"]
      }}
    />
  </ProductWrapper>
);

// const Cover = styled(Img)`
//   margin: var(--gutter) auto;
//   max-width: 100%;
//   display: block;
// `;

const Section = styled.section`
  margin-top: calc(var(--gutter) * 2);
  order: 10;

  @media (max-width: 767px) {
    margin-top: calc(var(--gutter) * 1);
  }
`;

const AccordionTitle = styled(H2)`
  ${Section} > & {
    @media (max-width: 767px) {
      ${AccordionStyle};
    }
  }
`;

class Accordion extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      closed: true
    };
  }

  toggle() {
    if (window.matchMedia("(max-width: 767px)").matches) {
      logEvent({
        category: "in-page",
        action: this.state.closed ? "open" : "close",
        label: this.props.children[1]
      });
      this.setState({ closed: !this.state.closed });
    }
  }

  render() {
    return (
      <AccordionTitle
        onClick={() => this.toggle()}
        closed={this.state.closed}
        {...this.props}
      />
    );
  }
}

class CustomElement extends React.Component {
  render() {
    const El =
      {
        "cc-figure": Figure,
        "cc-product": CCProduct,
        "gatsby-resp-iframe-wrapper": GatsbyIframe
      }[this.props.className] || React.Fragment;
    return <El {...this.props} />;
  }
}

/* eslint-disable new-cap */
const renderAst = new rehypeReact({
  createElement: React.createElement,
  components: {
    div: CustomElement,
    h1: H1,
    h2: Accordion,
    h3: H3,
    h4: H4,
    p: P,
    a: A,
    ol: Ol,
    ul: Ul,
    blockquote: Blockquote,
    section: Section
  },
  Fragment: React.Fragment
}).Compiler;
/* eslint-enable new-cap */

function sectioner(els, level = 2) {
  return els
    .reduce((a, c) => {
      if (c.value === "\n") return a;
      if (c.tagName === `h${level}`) a.push([]);
      if (Array.isArray(a[a.length - 1])) a[a.length - 1].push(c);
      else a.push(c);
      return a;
    }, [])
    .map(
      e =>
        !Array.isArray(e)
          ? e
          : {
              type: "element",
              tagName: "section",
              properties: { className: `level${level}` },
              children: level <= 6 ? sectioner(e, level + 1) : e
            }
    );
}

function logNavigation(slug) {
  logEvent({
    category: "in-page",
    action: "goto",
    label: slug
  });
}

function getTocSection(section, level = 2) {
  const title = section.children.filter(e => e.tagName === `h${level}`).shift();
  const text = title.children.filter(e => e.type === "text").shift();
  const slug = title.properties.id;
  const subSections = section.children.filter(e => e.tagName === "section");
  return text ? (
    <li key={text.value}>
      {slug ? (
        <a onClick={() => logNavigation(slug)} href={`#${slug}`}>
          {text.value}
        </a>
      ) : (
        text.value
      )}
      {subSections.length ? (
        <ul>{subSections.map(e => getTocSection(e, level + 1))}</ul>
      ) : null}
    </li>
  ) : null;
}

export default class Post extends React.Component {
  get metaText() {
    const { post } = this.props;
    return (
      <Meta>
        {post.author ? `${post.author} | ` : ""}
        <time dateTime={post.dateModified}>
          {formatDistanceToNow(new Date(post.dateModified), {
            addSuffix: true,
            locale: itLocale
          })}
        </time>
        <span> | {post.remark.timeToRead} minuti di lettura</span>
      </Meta>
    );
  }

  get toc() {
    const Title = H4.withComponent("h2");
    return (
      <Toc>
        <Title>In questo articolo troverai:</Title>
        <ul>
          {this.props.post.remark.htmlAst.children
            .filter(e => e.tagName === "section")
            .map(e => getTocSection(e))}
        </ul>
      </Toc>
    );
  }

  render() {
    const { post, products } = this.props;

    post.remark.htmlAst.children = sectioner(post.remark.htmlAst.children);

    return (
      <Wrapper className={post.category.slug}>
        <Helmet>
          <title>{post.alias || post.title}</title>
          <meta
            name="theme-color"
            content={getTheme(post.category.slug).main}
          />
        </Helmet>
        <H1>{post.title}</H1>
        {this.metaText}
        {/* <Cover sizes={post.hero.childImageSharp.sizes} /> */}
        {this.toc}
        {renderAst(post.remark.htmlAst)}
        <SliderEl
          products={products}
          renderProd={p => <ProductSmall product={p} scope="slider" />}
        />
        {/* <Top products={products} /> */}
      </Wrapper>
    );
  }
}
